import { useState } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import { nomeDaOrganizacao, useOrganizacoesDoAtendimento } from "../../nucleo/horas";
import { useAcessoDoServico, useDefinirAcessoDoServico } from "../../nucleo/operacao";
import { useCatalogoDeTickets, useEquipesDeAtendimento } from "../../nucleo/tickets";
import { Aviso, Botao, Campo, Cartao, Chip } from "../ui";

function alternar(lista: readonly string[], id: string): string[] {
  return lista.includes(id) ? lista.filter((item) => item !== id) : [...lista, id];
}

/**
 * O catálogo de serviços e quem usa cada um. Serviço sem restrição serve a todos; restrito a
 * organizações, só elas o veem e abrem chamado nele; restrito a equipes, só quem atende por elas
 * o usa ao abrir ou reclassificar um chamado.
 */
export function AcessoDosServicos() {
  const catalogo = useCatalogoDeTickets();
  const [escolhido, setEscolhido] = useState("");

  if (catalogo.servicos.isPending || catalogo.categorias.isPending) {
    return <p role="status">Carregando o catálogo…</p>;
  }
  if (catalogo.servicos.isError) {
    return <Aviso tom="erro">{mensagemDoConsole(catalogo.servicos.error)}</Aviso>;
  }
  const servicos = catalogo.servicos.data;
  const categorias = new Map((catalogo.categorias.data ?? []).map((item) => [item.id, item.nome]));
  const atual = servicos.find((item) => item.id === escolhido);

  return (
    <div data-testid="acesso-dos-servicos">
      <p className="nota-rodape mb-3">
        Por padrão todo serviço é aberto a todas as organizações e a toda a equipe. Restrinja para
        controlar quem vê e quem pode abrir chamado em cada um.
      </p>
      {servicos.length === 0 ? (
        <Aviso tom="marca">Nenhum serviço cadastrado ainda. Crie serviços pelo chamado, na gestão do catálogo.</Aviso>
      ) : (
        <ul className="lista-de-itens mb-4">
          {servicos.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="cartao bloco-link w-full text-left"
                aria-pressed={item.id === escolhido}
                onClick={() => setEscolhido(item.id)}
              >
                <strong>{item.nome}</strong>
                <span className="nota-rodape"> · {categorias.get(item.categoria_id) ?? "Sem categoria"}</span>{" "}
                {item.restrito_a_organizacoes ? <Chip estado="atencao">Organizações restritas</Chip> : null}{" "}
                {item.restrito_a_equipes ? <Chip estado="atencao">Equipes restritas</Chip> : null}
                {!item.restrito_a_organizacoes && !item.restrito_a_equipes ? (
                  <Chip estado="neutro">Aberto a todos</Chip>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      {atual ? <EditorDeAcesso key={atual.id} servicoId={atual.id} nome={atual.nome} /> : null}
    </div>
  );
}

function EditorDeAcesso({ servicoId, nome }: { servicoId: string; nome: string }) {
  const acesso = useAcessoDoServico(servicoId);
  const organizacoes = useOrganizacoesDoAtendimento();
  const equipes = useEquipesDeAtendimento();
  const salvar = useDefinirAcessoDoServico(servicoId);
  const [orgs, setOrgs] = useState<string[] | null>(null);
  const [times, setTimes] = useState<string[] | null>(null);
  const [filtro, setFiltro] = useState("");

  if (acesso.isPending) return <p role="status">Carregando o acesso de {nome}…</p>;
  if (acesso.isError) return <Aviso tom="erro">{mensagemDoConsole(acesso.error)}</Aviso>;

  const orgsMarcadas = orgs ?? acesso.data.organizacoes;
  const timesMarcados = times ?? acesso.data.equipes;
  const visiveis = (organizacoes.data ?? []).filter((item) =>
    nomeDaOrganizacao(item).toLocaleLowerCase("pt-BR").includes(filtro.trim().toLocaleLowerCase("pt-BR")),
  );

  return (
    <Cartao titulo={`Quem usa “${nome}”`} data-testid="editor-de-acesso">
      <h4 className="titulo-secao">Organizações</h4>
      <p className="nota-rodape mb-2">
        {orgsMarcadas.length === 0
          ? "Nenhuma marcada: todas as organizações veem este serviço."
          : `${orgsMarcadas.length} marcada(s): só elas veem este serviço.`}
      </p>
      <Campo rotulo="Filtrar organizações" value={filtro} onChange={(evento) => setFiltro(evento.target.value)} />
      <ul className="mb-4 max-h-60 overflow-auto" aria-label="Organizações">
        {visiveis.map((item) => (
          <li key={item.id}>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={orgsMarcadas.includes(item.id)}
                onChange={() => setOrgs(alternar(orgsMarcadas, item.id))}
              />
              {nomeDaOrganizacao(item)}
            </label>
          </li>
        ))}
      </ul>

      <h4 className="titulo-secao">Equipes de atendimento</h4>
      <p className="nota-rodape mb-2">
        {timesMarcados.length === 0
          ? "Nenhuma marcada: toda a equipe usa este serviço."
          : "Só quem atende por uma das equipes marcadas usa este serviço. A administração do catálogo fica de fora da trava."}
      </p>
      <ul className="mb-4" aria-label="Equipes">
        {(equipes.data ?? []).map((item) => (
          <li key={item.id}>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={timesMarcados.includes(item.id)}
                onChange={() => setTimes(alternar(timesMarcados, item.id))}
              />
              {item.nome}
            </label>
          </li>
        ))}
      </ul>

      {salvar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(salvar.error)}</Aviso> : null}
      {salvar.isSuccess ? <Aviso tom="sucesso" className="mb-3">Acesso do serviço salvo.</Aviso> : null}
      <Botao
        disabled={salvar.isPending}
        onClick={() => salvar.mutate({ organizacoes: orgsMarcadas, equipes: timesMarcados })}
      >
        {salvar.isPending ? "Salvando…" : "Salvar acesso"}
      </Botao>
    </Cartao>
  );
}
